import React from "react"
import { Pagination as MuiPagination, Select, MenuItem, Typography, SelectChangeEvent } from "@mui/material"
import useGetDeviceType from "../../hooks/useGetDeviceType"
import styles from "./Pagination.module.scss"

export interface PaginationProps {
    pageNo: number
    setPageNo: (page: number) => void
    totalPage: number
    pageSize?: number
    setPageSize?: (value: number) => void
    expandPagination?: boolean
}

const pageSizeOptions = [10, 15, 30, 45, 60]

const Pagination: React.FC<PaginationProps> = ({
    pageNo,
    setPageNo,
    totalPage,
    pageSize = 10,
    setPageSize,
    expandPagination,
}) => {
    const { deviceType } = useGetDeviceType()

    const handleChangePage = (_: React.ChangeEvent<unknown>, value: number) => {
        setPageNo(value)
    }

    const handleChangePageSize = (event: SelectChangeEvent) => {
        const value = parseInt(event.target.value, 10)
        if (setPageSize) {
            setPageSize(value)
            setPageNo(1)
        }
    }

    if (totalPage === 0) return null

    const renderDesktopPagination = () => (
        <div className={`${styles.pagination_view} ${expandPagination ? styles.pagination_view__expanded : ""}`}>
            <div className={styles.pages_list}>
                <Typography variant="body2" className={styles.showing_text}>
                    Showing
                </Typography>
                <Select
                    value={pageSize.toString()}
                    onChange={handleChangePageSize}
                    className={styles.select}
                    disableUnderline
                    variant="standard"
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 200,
                                width: 60,
                            },
                        },
                    }}>
                    {pageSizeOptions.map(size => (
                        <MenuItem key={size} value={size.toString()}>
                            {size}
                        </MenuItem>
                    ))}
                </Select>
            </div>

            <div
                className={`${styles.pagination_content} ${expandPagination ? styles.pagination_content__static : ""}`}>
                <MuiPagination
                    count={totalPage}
                    page={pageNo}
                    onChange={handleChangePage}
                    size="small"
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                    shape="rounded"
                    className={styles.muiPagination}
                    sx={{
                        "& .MuiPaginationItem-root": {
                            fontSize: "12px",
                            minWidth: 20,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 1,
                            color: "#70758D",
                            fontWeight: 500,
                            "&.Mui-selected": {
                                color: "#FC441E",
                                fontWeight: 700,
                                backgroundColor: "transparent",
                            },
                        },
                        "& .MuiPaginationItem-ellipsis": {
                            fontSize: "12px",
                            lineHeight: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        },
                    }}
                />
            </div>
        </div>
    )

    const renderMobilePagination = () => (
        <div className={styles.mweb_pagination_container}>
            <span className={styles.prev_container} onClick={() => pageNo > 1 && setPageNo(pageNo - 1)}>
                <span style={{ color: pageNo === 1 ? "#B7BAC6" : "#FC441E" }}>Prev</span>
            </span>
            <span className={styles.current_page}>
                <span>{pageNo}</span>
            </span>
            <span className={styles.next_container} onClick={() => pageNo < totalPage && setPageNo(pageNo + 1)}>
                <span style={{ color: pageNo === totalPage ? "#B7BAC6" : "#FC441E" }}>Next</span>
            </span>
        </div>
    )

    if (deviceType === "Desktop") {
        return renderDesktopPagination()
    }

    if (deviceType === "Mobile") {
        return renderMobilePagination()
    }

    return null
}

export default Pagination
